import React, { useState, useEffect, useRef } from 'react'
import SweetAlert from 'react-bootstrap-sweetalert'
import ReactGA from 'react-ga4'
import { Helmet } from 'react-helmet'
import Loadable from 'react-loadable'
import { connect, useDispatch } from 'react-redux'
import { Route, Switch, Redirect, withRouter, matchPath } from 'react-router-dom'
import { toast } from 'react-toastify'
import { compose } from 'redux'
import {
  fetchCartItems,
  fetchPortal,
  fetchUser,
  fetchUserCredit,
  setLoading,
  fetchCustomerAddresses,
  fetchManagedCustomerAddresses,
  fetchAddressesByPortalConfig,
  clearCoupons
} from './actions'
import { setSessionToken } from './actions/sessions'
import samplePortalCss from './assets/scss/sample_portal_css.css'
import CartAccessHandler from './components/Cart/CartAccessHandler'
import CartRedirectHandler from './components/Cart/CartRedirectHandler'
import FeatureFlaggedRoute from './components/FeatureFlaggedRoute'
import loadCheckoutPageV2 from './v2/checkout/preloadCheckoutPage'

const CartRedirectHandlerV2 = React.lazy(() => import('./v2/cart/components/CartRedirectHandler'))
const CartPageV2 = React.lazy(() => import('./v2/cart/components/CartPage'))
const CheckoutPageV2 = React.lazy(loadCheckoutPageV2)

// ** Components for routes **
import CheckoutAccessHandler from './components/Checkout/CheckoutAccessHandler'
import { setAppliedCoupon } from './components/Checkout/checkoutFunctions'
import CheckoutRedirectHandler from './components/Checkout/CheckoutRedirectHandler'
import ContentLoader from './components/global/loaders/ContentLoader'
const ContactPage = React.lazy(() => import('./components/Contact/ContactPage'))
const Dashboard = React.lazy(() => import('./components/CustomerAccount/Dashboard/Dashboard'))
const Invoice = React.lazy(() => import('./components/Checkout/final-receipt/Invoice'))
//layout
import HeaderOne from './components/Header/HeaderOne'
import FooterOne from './components/layouts/footers/FooterOne'
import Loading from './components/layouts/LoadingFallback'
import HomePage from './components/PortalPages/HomePage'
import PortalPage from './components/PortalPages/PortalPage'
const PageNotFound = React.lazy(() => import('./components/PageNotFound'))
import ProductDetails from './components/Shop/ProductDetails'
import ProductList from './components/Shop/ProductList'
import RecordingPage from './components/Recording/RecordingPage'
import SsoSignInPage from './components/Sso/SsoSignInPage'
const SignInWrapper = React.lazy(() => import('./components/UserPages/SignInWrapper'))
import AdminUserApp from './components/AdminUser/AdminUserApp'

const EditContactInformation = React.lazy(() => import('./components/CustomerAccount/Dashboard/EditContactInformation'))
const SocialConnections = React.lazy(() => import('./components/CustomerAccount/Dashboard/SocialConnections'))
const SocialConnectionCallback = React.lazy(
  () => import('./components/CustomerAccount/Dashboard/social/SocialConnectionCallback')
)
const CustomerAddress = React.lazy(() => import('./components/CustomerAccount/Dashboard/CustomerAddress'))
const MyOrders = React.lazy(() => import('./components/CustomerAccount/Dashboard/MyOrders'))
const MyCredit = React.lazy(() => import('./components/CustomerAccount/Dashboard/MyCredit'))
const MyDownloads = React.lazy(() => import('./components/CustomerAccount/Dashboard/MyDownloads'))
const MySocialPosts = React.lazy(() => import('./components/CustomerAccount/Dashboard/MySocialPosts'))
const MyOrderShow = React.lazy(() => import('./components/CustomerAccount/Dashboard/MyOrderShow'))

import { useRedirectTo } from './hooks/useRedirectTo'
import { useRecordingNavigation } from './hooks/useRecordingNavigation'
import { useStoreCheckoutFlag } from './hooks/useStoreCheckoutFlag'
import {
  fetchCurrentStoreView,
  initializeCurrentStoreView
} from './redux/storeConfig/slices/slice.storeViews/storeViews.thunks.js'
import { canUseCurrentStoreView } from './redux/storeConfig/slices/slice.storeViews/storeViews.utils.js'

// css
import './lib/embryoCss.js'
import 'react-toastify/dist/ReactToastify.css'
import './assets/scss/bootstrap/bootstrap.css'

import { legacyLogOut, enterpriseLogOut } from './util/utils'
import { withJamParams } from './util/recordingNavigation'

const Maintenance = Loadable({ loader: () => import('./components/Maintenance/MaintenanceMode'), loading: Loading })

toast.configure({
  autoClose: 2500,
  draggable: true,
  position: 'top-center'
})

const App = props => {
  const [portalLoading, setPortalLoading] = useState(true)
  const [metaTags, setMetaTags] = useState([])
  const [showConfirmationWall, setShowConfirmationWall] = useState(false)
  const [sessionCustomCss, setSessionCustomCss] = useState(null)
  const [isPrivatizedLogin, setIsPrivatizedLogin] = useState(false)

  const { location, currentLocation, portal, pages, loading, currentUser, locations } = props
  const homePage = currentLocation?.home_page
  const hasAuthToken = !!(props.session?.rawToken || localStorage.getItem('authToken'))
  const canUseStoreView = canUseCurrentStoreView({
    hasAuthToken,
    currentUser,
    currentStoreView: currentLocation,
    availableStoreViews: locations
  })
  const checkoutFlag = useStoreCheckoutFlag(location)
  const isCheckoutV2 = checkoutFlag.status === 'ready' && checkoutFlag.enabled

  const { redirectUrl, executeRedirect, generateLoginUrl } = useRedirectTo(portal)
  useRecordingNavigation()

  const dispatch = useDispatch()
  const initializedPortal = useRef(null)

  useEffect(() => {
    // The store view starts only after checkout has a successful flag decision.
    if (!portal?.id || checkoutFlag.status !== 'ready' || initializedPortal.current === portal.id) return
    initializedPortal.current = portal.id
    dispatch(initializeCurrentStoreView(portal))
  }, [dispatch, portal, checkoutFlag.status])

  // If available store views change and the current is not in the new list, fetch the new current store view
  useEffect(() => {
    if (!currentLocation?.id) {
      return
    }

    if (locations?.length > 0 && !locations.map(l => l.id).includes(currentLocation?.id)) {
      dispatch(fetchCurrentStoreView(locations[0].id))
    }
  }, [locations, currentLocation, dispatch])

  // Apply custom portal CSS after portal is loaded
  useEffect(() => {
    if (portal.custom_css_block && portal.custom_css_block !== sessionCustomCss) {
      setSessionCustomCss(portal.custom_css_block)
      applyCustomCss(portal.custom_css_block)
    }
  }, [portal.custom_css_block])

  const applyCustomCss = cssText => {
    const styleId = 'custom-css-style'
    let styleElement = document.getElementById(styleId)
    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = styleId
      document.head.appendChild(styleElement)
    }
    styleElement.innerHTML = cssText
  }

  // Fetch portal data on load
  useEffect(() => {
    props
      .fetchPortal()
      .then(response => {
        // Google Analytics - Use response data directly since Redux state might not be updated yet
        const gaTrackingCode = response?.google_analytics_tracking_code
        if (response?.has_google_analytics && gaTrackingCode) {
          // Loads gtag.js and issues a single config command for this portal's property, which
          // sends the initial page_view by itself. Duplicating either step here would make the
          // portal's property record the same page view multiple times.
          // Any event added later must pass send_to: gaTrackingCode, otherwise it is also
          // routed to our internal Marketfuel property configured in index.html
          ReactGA.initialize(gaTrackingCode)
        }
        setPortalLoading(false)
      })
      .catch(error => {
        console.error('Error fetching portal', error)
        props.history.push('/not_found')
      })
  }, [])

  // On app load, check localStorage for session token
  // and add event listener for postMessage to receive token from internal links that pass it
  // if logout=true is passed in the URL, clear the session token
  useEffect(() => {
    // Check if logout=true is passed in the URL
    const urlParams = new URLSearchParams(window.location.search)
    const logout = urlParams.get('logout')
    if (logout === 'true') {
      legacyLogOut()
      const isEnterpriseLogout = enterpriseLogOut()
      props.setLoading('session', false)
      // remove logout from params
      if (!isEnterpriseLogout) {
        props.history.push(withJamParams('/log-in', window.location.search))
      }
      return
    }

    // Check localStorage for session token
    const sessionToken = localStorage.getItem('authToken')
    if (sessionToken && !props.session) {
      props.setSessionToken(sessionToken)
    } else if (!sessionToken && props.loading.session) {
      props.setLoading('session', false)
    }

    // Handler for receiving postMessage, for when app is opened in new tab via link
    const handlePostMessage = event => {
      const allowedDomain = window.location.origin // only accept posts from the same domain

      if (event.origin === allowedDomain) {
        if (event.data.token) {
          localStorage.setItem('authToken', event.data.token) // Save the passed token to localStorage
          props.setSessionToken(event.data.token) // Update session token in Redux
        }
      }
    }

    // Add event listener for postMessage
    window.addEventListener('message', handlePostMessage, false)

    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener('message', handlePostMessage, false)
    }
  }, [])

  // fetch user after session is fetched
  // or when portal/location is fetched/changed
  useEffect(() => {
    if (props.session) {
      props
        .fetchUser()
        .catch(e => {
          console.error('Error fetching User')
        })
        .finally(() => {
          props.setLoading('session', false)
        })
    }
  }, [currentLocation?.id, props.session, props.fetchUser, props.loading.session])

  // On User change
  useEffect(() => {
    if (currentUser.id && props.portal.id) {
      checkConfirmationWall()
    }
  }, [currentUser.id, props.portal.id])

  // On User or Location change
  useEffect(() => {
    // if location has been fetched and the user doesn't have access, prevent stale cart by clearing it
    if (hasAuthToken && currentLocation?.id && !canUseStoreView) {
      dispatch({ type: 'CLEAR_CART' })
      dispatch({ type: 'SET_USER_CART_ID', payload: null })
      dispatch({ type: 'CHECKOUT_SESSION_TOKEN/SET_SHORT_CODE', payload: null })
      return
    }

    if (currentUser.id) {
      // For logged in users, fetch address info based on portal configuration
      props.fetchAddressesByPortalConfig(props.portal)

      if (canUseStoreView) {
        props.fetchUserCredit()
      }
    }
    if (canUseStoreView && checkoutFlag.status === 'ready' && !isCheckoutV2) {
      fetchCartAndClearCoupons()
    }
  }, [currentUser.id, currentLocation?.id, canUseStoreView, hasAuthToken, isCheckoutV2, checkoutFlag.status, dispatch])

  // On Portal load
  useEffect(() => {
    if (portal && portal.id) {
      buildMetaTags(portal)
      buildFavicon()
      checkConfirmationWall()
      setIsPrivatizedLogin(portal.portal_has_privatized_login_page)
    }
  }, [dispatch, portal.id])

  const checkConfirmationWall = () => {
    // Confirmation Wall
    if (portal.portal_has_confirmation_wall) {
      const token = localStorage.getItem('confirmation_wall_accepted_at')
      const tokenIsExpired = new Date(token) < new Date(new Date().getTime() - 24 * 60 * 60 * 1000)

      // if user is logged in and token is present, ignore expiry
      if (props.currentUser.id && token) {
        localStorage.setItem('confirmation_wall_accepted_at', new Date())
        showConfirmationWall && setShowConfirmationWall(false)
      }
      // if token is not present, always ask to confirm
      else if (!token) {
        !showConfirmationWall && setShowConfirmationWall(true)
      }
      // if token is expired, only ask to confirm if user is not logged in
      else if (tokenIsExpired && !props.currentUser.id) {
        !showConfirmationWall && setShowConfirmationWall(true)
      }
    }
  }

  const handleAcceptTerms = () => {
    const now = new Date()
    localStorage.setItem('confirmation_wall_accepted_at', now)
    setShowConfirmationWall(false)
  }

  const fetchCartAndClearCoupons = () => {
    props.fetchCartItems().then(() => {
      props.setAppliedCoupon('')
      props.clearCoupons()
    })
  }

  const buildFavicon = () => {
    if (props.portal.length === 0) {
      return []
    }
    const favicon = document.getElementById('favicon')
    if (favicon && props.portal.favicon) {
      favicon.href = props.portal.favicon.medium.url
    }
  }

  const renderSweetAlert = () => {
    return (
      <>
        {/* Confirmation Wall overrides normal SweetAlert unless accepted */}
        {showConfirmationWall ? (
          <SweetAlert
            title=""
            onConfirm={handleAcceptTerms}
            onCancel={() => {
              window.location.href = 'https://www.google.com'
            }}
            showCancel={true}
            confirmBtnCssClass="mf-primary-btn"
            confirmBtnText="I Accept"
            closeOnClickOutside={false}
          >
            {portal.confirmation_wall_message}
          </SweetAlert>
        ) : (
          <SweetAlert
            {...props.sweetAlert}
            // default CSS style for confirm button
            confirmBtnCssClass={props.sweetAlert.confirmBtnCssClass || 'mf-primary-btn'}
          >
            {props.sweetAlert.alertMessage}
          </SweetAlert>
        )}
      </>
    )
  }

  const buildMetaTags = portal => {
    const newMetaTags = resolveMetaTags(portal)
    setMetaTags(newMetaTags)
  }

  const renderHelmet = portal => {
    let metaTitle = false

    const handleStylesheetError = () => {
      console.error('Error loading custom CSS. Refreshing page.')
      window.location.reload()
    }

    return (
      <Helmet>
        {/* Add Portal CSS */}
        {portal && portal.stylesheet ? (
          <link rel="stylesheet" href={`/css/${portal.stylesheet}`} onError={handleStylesheetError} />
        ) : null}

        {/* Fall back to sample CSS if portal has no custom CSS block */}
        {portal ? <style type="text/css">{sessionCustomCss || `${samplePortalCss}`}</style> : null}

        {metaTags.map(tag => {
          if (tag.property === 'description') {
            return <meta name={tag.property} content={tag.value} key={tag.property} />
          } else if (tag.property !== 'title') {
            return <meta property={tag.property} content={tag.value} key={tag.property} />
          } else {
            metaTitle = true
            return <title key={tag.property}>{tag.value}</title>
          }
        })}
        {!metaTitle ? <title key={portal.name}>{portal.name}</title> : null}
      </Helmet>
    )
  }

  const shouldHideHeaderFooter = location => {
    const hideForPaths = [
      '/forgot-password',
      '/password-expired',
      '/reset-password/:token',
      '/sign-up',
      '/send-confirmation',
      '/confirm-email',
      '/log-in',
      '/help-signing-in',
      '/auth0_callback'
    ]

    // hide if user is being logged in
    if (props.sessionStatus === 'LOADING') return true

    return hideForPaths.some(path => {
      const match = matchPath(location.pathname, {
        path,
        exact: true
      })
      return match && match.isExact
    })
  }

  const userIsLoggedIn = currentUser?.id
  const hasAdminAccess = currentUser?.is_admin_user
  const loginUrl = `/${portal.custom_login_url || 'log-in'}`
  const hideHeaderFooter = shouldHideHeaderFooter(location)

  const appLoading =
    portalLoading || loading.customerGroups || loading.signIn || loading.session || !props.currentLocation?.id

  useEffect(() => {
    // Only execute redirect after portal is loaded and app is not loading
    if (!appLoading && portal?.id && redirectUrl) {
      // Check if user is logged in
      if (currentUser?.id) {
        // User is logged in, execute redirect immediately
        executeRedirect()
      } else if (location.pathname !== loginUrl) {
        // User not logged in, redirect to login with redirect_to preserved
        const nextLoginUrl = generateLoginUrl(redirectUrl)
        props.history.push(nextLoginUrl)
      }
    }
  }, [
    appLoading,
    currentUser.id,
    executeRedirect,
    generateLoginUrl,
    location.pathname,
    loginUrl,
    portal.id,
    props.history,
    redirectUrl
  ])

  if (checkoutFlag.status === 'error' && !props.currentLocation?.id) {
    return (
      <div role="alert">
        Could not load checkout options. <button onClick={checkoutFlag.retry}>Retry</button>
      </div>
    )
  }

  if (appLoading && window.location.pathname !== '/not_found') {
    return <ContentLoader />
  } else if (portal.maintenance_mode) {
    return <Maintenance portal={portal} />
  }

  const isAdminUserPath = !!matchPath(location.pathname, {
    path: '/admin',
    exact: false
  })

  if (hasAdminAccess && isAdminUserPath) {
    return <AdminUserApp renderSweetAlert={renderSweetAlert} />
  }

  return (
    <div id="app-wrapper">
      {renderHelmet(portal)}

      {!hideHeaderFooter && <HeaderOne checkoutFlag={checkoutFlag} />}

      <main className="main-content">
        <div className="app-container">
          <div>{/* dummy div needed for portal accent stripe */}</div>
          <Switch>
            {/* Common (Always Public) Routes */}
            <Route exact path="/not_found" component={PageNotFound} />
            <Route exact path="/record" render={() => <RecordingPage supportEmail={portal.contact_email} />} />
            <Route exact path="/sso-log-in/:sso_id/:sso_token" component={SsoSignInPage} />
            <Route
              path={[
                '/forgot-password',
                '/password-expired',
                '/reset-password/:token',
                '/sign-up',
                '/send-confirmation',
                '/confirm-email',
                '/help-signing-in',
                '/auth0_callback'
              ]}
              render={props => <SignInWrapper {...props} homePage={homePage} publicPage={!isPrivatizedLogin} />}
            />

            {/* Use different sign-in page for private portals */}
            <Route
              exact
              path={loginUrl}
              render={props => <SignInWrapper {...props} homePage={homePage} publicPage={!isPrivatizedLogin} />}
            />

            {/* Private portals redirect when not logged in */}
            {isPrivatizedLogin && !userIsLoggedIn && !props.loading.session && (
              <Route path="/">
                <Redirect to={generateLoginUrl(`${location.pathname}${location.search}${location.hash || ''}`)} />
              </Route>
            )}

            {/* If portal is private, user must be logged in to access these routes */}
            {isPrivatizedLogin && !userIsLoggedIn ? null : (
              <>
                <Route
                  exact
                  path="/customer/account/dashboard"
                  render={props => <Dashboard {...props} currentLocation={currentLocation} />}
                />
                <Route exact path="/customer/account/profile" component={EditContactInformation} />
                <Route exact path="/customer/account/social" component={SocialConnections} />
                <Route exact path="/social-connection/callback" component={SocialConnectionCallback} />
                <Route exact path="/customer/account/addresses" component={CustomerAddress} />
                <Route exact path="/customer/account/orders" component={MyOrders} />
                <Route exact path="/customer/account/credit-balance" component={MyCredit} />
                <Route exact path="/customer/account/downloads" component={MyDownloads} />
                <Route exact path="/customer/account/social-posts" component={MySocialPosts} />
                <Route path="/order-details/:orderNumber/:orderId" component={MyOrderShow} />
                <Route path="/product/:product_id/:portal_id/:location_id" component={ProductDetails} />
                {/* <Route path="/product/:product_id/:portal_id" component={ProductDetails} /> */}
                <Route exact path="/check-out" component={CheckoutRedirectHandler} />
                <Route exact path="/checkout" component={CheckoutRedirectHandler} />
                <Route
                  path="/checkout/:shortCode"
                  render={routeProps => (
                    <FeatureFlaggedRoute
                      decision={checkoutFlag}
                      enabledComponent={CheckoutPageV2}
                      fallbackComponent={CheckoutAccessHandler}
                      componentProps={{ ...routeProps, currentLocation }}
                    />
                  )}
                />
                <Route path="/contact-us" component={ContactPage} />
                <Route
                  exact
                  path="/shop"
                  render={props => <ProductList {...props} currentLocation={currentLocation} />}
                />
                <Route
                  exact
                  path="/shop/:category_id"
                  render={props => <ProductList {...props} currentLocation={currentLocation} />}
                />
                <Route
                  exact
                  path="/shop/tag_filters/:tag_filter_id"
                  render={props => <ProductList {...props} currentLocation={currentLocation} />}
                />
                <Route path="/shop?" render={props => <ProductList {...props} currentLocation={currentLocation} />} />
                <Route
                  exact
                  path="/cart"
                  render={routeProps => (
                    <FeatureFlaggedRoute
                      decision={checkoutFlag}
                      enabledComponent={CartRedirectHandlerV2}
                      fallbackComponent={CartRedirectHandler}
                      componentProps={{ ...routeProps, currentLocation }}
                    />
                  )}
                />
                <Route
                  path="/cart/:shortCode"
                  render={routeProps => (
                    <FeatureFlaggedRoute
                      decision={checkoutFlag}
                      enabledComponent={CartPageV2}
                      fallbackComponent={CartAccessHandler}
                      componentProps={{ ...routeProps, currentLocation }}
                    />
                  )}
                />
                <Route exact path="/confirmation" component={Invoice} />

                {/* Portal pages — already scoped to this location by the backend */}
                {pages
                  .filter(page => page.page_type !== 'Home')
                  .map(page => (
                    <Route
                      path={page.active_url}
                      key={page.id}
                      render={props => <PortalPage {...props} page={page} />}
                    />
                  ))}
                {homePage && (
                  <Route exact path={homePage.active_url} render={props => <HomePage {...props} page={homePage} />} />
                )}
              </>
            )}

            {/* Fallback Routes */}
            <Route component={PageNotFound} />
          </Switch>
        </div>

        {renderSweetAlert()}
      </main>
      {!hideHeaderFooter && <FooterOne />}
    </div>
  )
}

const mapStateToProps = state => {
  return {
    currentLocation: state.storeViews.current,
    currentUser: state.currentUser,
    loading: state.loading,
    locations: state.storeViews.available,
    pages: state.storeViews.current?.pages || [],
    portal: state.portal,
    session: state.session,
    sessionStatus: state.status,
    sweetAlert: state.sweetAlert,
    userCredits: state.userCredits
  }
}

export default compose(
  withRouter,
  connect(mapStateToProps, {
    fetchPortal,
    fetchUserCredit,
    fetchUser,
    fetchCartItems,
    clearCoupons,
    setAppliedCoupon,
    fetchCustomerAddresses,
    fetchManagedCustomerAddresses,
    fetchAddressesByPortalConfig,
    setSessionToken,
    setLoading
  })
)(App)

const resolveMetaTags = portal => {
  if (!portal || portal.length === 0) return []

  const pushToTags = (value, properties) => {
    properties.forEach(property => {
      newMetaTags.push({ value, property })
    })
  }

  const newMetaTags = []

  const mappings = [
    { valueKey: 'meta_title', properties: ['title', 'og:title', 'twitter:title'] },
    { valueKey: 'meta_description', properties: ['description', 'og:description', 'twitter:description'] },
    { valueKey: 'meta_locale', properties: ['og:locale'] },
    { valueKey: 'meta_site_name', properties: ['og:site_name'] },
    { valueKey: 'meta_type', properties: ['og:type'] },
    { valueKey: 'meta_twitter_type', properties: ['twitter:type'] },
    { valueKey: 'meta_image_alt', properties: ['og:image:alt'] }
  ]

  mappings.forEach(mapping => {
    const value = portal[mapping.valueKey]
    if (value) {
      pushToTags(value, mapping.properties)
    }
  })

  if (portal.meta_image && portal.meta_image.small && portal.meta_image.small.url) {
    pushToTags(portal.meta_image.small.url, ['twitter:image', 'og:image'])
  }

  return newMetaTags
}
